import { useEffect, useRef, useState } from 'react'
import type { VericredV8RateLimitsResponse } from '@ideon-api/sdk-internal'
import { parsedSdkErrorBody, useAuthServiceClient } from './sdk-client'

export interface PeriodBreach {
  count: number
  lastAt: string | null
}

export interface RecentBreach {
  /** Start of a UTC minute that had one or more 429s. */
  minute: string
  count: number
}

export interface RateLimitRow {
  appId: string
  /** Named failure for this app id (`upstream_error`, `not_found`, ...); null on a healthy row. */
  failure: string | null
  /** Cap per period; a null cap means unlimited, an undefined cap was of the wrong type. Null when the row failed. */
  limits: Record<string, number | null | undefined> | null
  trackingEnabled: boolean | null
  usage: Record<string, number> | null
  breaches: Record<string, PeriodBreach> | null
  /** Minutes of the last hour with a 429, oldest first. Null when unknown: a failed row or an older vericred. */
  recentBreaches: RecentBreach[] | null
  /** When the customer record last changed in vericred -- not the last 3Scale sync. */
  customerUpdatedAt: string | null
}

export interface RateLimitsMeta {
  appIdCount: number
  failedCount: number
}

export type RateLimitsState =
  | { status: 'loading' }
  | { status: 'ready'; rows: RateLimitRow[]; meta: RateLimitsMeta }

export type CallRateLimits = (
  applicationId: string,
  options: { acceptVersion: string; baseUrl: string }
) => Promise<VericredV8RateLimitsResponse>

export interface RateLimitsDeps {
  callRateLimits?: CallRateLimits
  getVericredBaseUrl?: () => Promise<string | null>
}

// Vericred answers 404 without this header.
const ACCEPT_VERSION = 'v8'
// One request per id; the cap keeps a many-application account from flooding vericred.
const MAX_QUERIED_IDS = 25

type Wire = Record<string, unknown>

function isObject(value: unknown): value is Wire {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function mapRecord<T>(value: unknown, pick: (entry: unknown) => T | undefined): Record<string, T> | null {
  if (!isObject(value)) return null
  const out: Record<string, T> = {}
  for (const [period, entry] of Object.entries(value)) {
    const picked = pick(entry)
    if (picked !== undefined) out[period] = picked
  }
  return out
}

// A null cap is unlimited. A cap of the wrong type is unknown, so it keeps its key with no value:
// the period still lists, shows "—", and never reads as unlimited.
function mapCaps(value: unknown): Record<string, number | null | undefined> | null {
  if (!isObject(value)) return null
  return Object.fromEntries(
    Object.entries(value).map(([period, cap]) => [period, typeof cap === 'number' || cap === null ? cap : undefined])
  )
}

function mapRecentBreaches(value: unknown): RecentBreach[] | null {
  if (!Array.isArray(value)) return null
  return value.flatMap((entry) =>
    isObject(entry) && typeof entry.minute === 'string' && typeof entry.count === 'number'
      ? [{ minute: entry.minute, count: entry.count }]
      : []
  )
}

const EMPTY_ROW = {
  limits: null,
  trackingEnabled: null,
  usage: null,
  breaches: null,
  recentBreaches: null,
  customerUpdatedAt: null,
}

function failedRow(appId: string, failure: string): RateLimitRow {
  return { appId, failure, ...EMPTY_ROW }
}

// Tolerant of null sections: null means unknown, `{}`/`[]` means none. The SDK camelCases the
// top level only; `breaches` is untyped there, so its entries keep the server's `last_at`.
function mapRow(appId: string, response: unknown): RateLimitRow {
  const wire: Wire = isObject(response) ? response : {}
  return {
    appId,
    failure: null,
    limits: mapCaps(wire.limits),
    trackingEnabled: typeof wire.trackingEnabled === 'boolean' ? wire.trackingEnabled : null,
    usage: mapRecord(wire.usage, (count) => (typeof count === 'number' ? count : undefined)),
    breaches: mapRecord(wire.breaches, (entry) =>
      isObject(entry) && typeof entry.count === 'number'
        ? { count: entry.count, lastAt: typeof entry.last_at === 'string' ? entry.last_at : null }
        : undefined
    ),
    recentBreaches: mapRecentBreaches(wire.recentBreaches),
    customerUpdatedAt: typeof wire.customerUpdatedAt === 'string' ? wire.customerUpdatedAt : null,
  }
}

function failureName(error: unknown): string {
  if (isObject(error) && error.name === 'TimeoutError') return 'timeout'
  switch (parsedSdkErrorBody(error).status) {
    case 401:
      return 'unauthorized'
    case 403:
      return 'forbidden'
    case 404:
      return 'not_found'
    default:
      return 'upstream_error'
  }
}

function buildState(rows: RateLimitRow[]): RateLimitsState {
  return {
    status: 'ready',
    rows,
    meta: { appIdCount: rows.length, failedCount: rows.filter((row) => row.failure).length },
  }
}

const CONFIGS_TIMEOUT_MS = 10_000
const LOCAL_HOSTS = ['localhost', '127.0.0.1']

// The bearer token rides every call, so only https (or http to a local host) is accepted.
function safeBaseUrl(value: unknown): string | null {
  if (typeof value !== 'string') return null
  try {
    const url = new URL(value)
    const local = url.protocol === 'http:' && LOCAL_HOSTS.includes(url.hostname)
    return url.protocol === 'https:' || local ? value.replace(/\/+$/, '') : null
  } catch {
    return null
  }
}

async function defaultVericredBaseUrl(): Promise<string | null> {
  const response = await fetch('/configs', { signal: AbortSignal.timeout(CONFIGS_TIMEOUT_MS) })
  if (!response.ok) return null
  const body: unknown = await response.json()
  return isObject(body) ? safeBaseUrl(body.vericred_base_url) : null
}

async function loadRows(ids: string[], deps: Required<RateLimitsDeps>): Promise<RateLimitRow[]> {
  const baseUrl = await deps.getVericredBaseUrl().catch(() => null)
  if (!baseUrl) return ids.map((id) => failedRow(id, 'not_configured'))

  const queried = ids.slice(0, MAX_QUERIED_IDS)
  const settled = await Promise.allSettled(
    // async, so a synchronous throw from the caller becomes a rejection for this id only
    queried.map(async (id) => deps.callRateLimits(id, { acceptVersion: ACCEPT_VERSION, baseUrl }))
  )
  const rows = settled.map((result, index) =>
    result.status === 'fulfilled'
      ? mapRow(queried[index], result.value)
      : failedRow(queried[index], failureName(result.reason))
  )
  return [...rows, ...ids.slice(MAX_QUERIED_IDS).map((id) => failedRow(id, 'not_queried'))]
}

export function useAccountRateLimits(applicationIds: string[], deps: RateLimitsDeps = {}): RateLimitsState {
  const getClient = useAuthServiceClient()
  const [state, setState] = useState<RateLimitsState>({ status: 'loading' })

  const resolved: Required<RateLimitsDeps> = {
    callRateLimits:
      deps.callRateLimits ??
      (async (applicationId, { acceptVersion, baseUrl }) => {
        const client = await getClient()
        return client.quote.getRateLimits(applicationId, { acceptVersion }, { baseUrl })
      }),
    getVericredBaseUrl: deps.getVericredBaseUrl ?? defaultVericredBaseUrl,
  }
  // The effect keys on the ids' content; the latest callers ride a ref so a new closure never refetches.
  const depsRef = useRef(resolved)
  depsRef.current = resolved

  const idsKey = JSON.stringify([...new Set(applicationIds)])

  useEffect(() => {
    const ids = JSON.parse(idsKey) as string[]
    let cancelled = false
    setState({ status: 'loading' })
    if (ids.length === 0) {
      setState(buildState([]))
      return
    }
    void loadRows(ids, depsRef.current).then((rows) => {
      if (!cancelled) setState(buildState(rows))
    })
    return () => {
      cancelled = true
    }
  }, [idsKey])

  return state
}
